/**
 * Wires the design system's form controls (Web Awesome, ADR-0020) into a page: what the
 * components leave to the page. Each entry point imports the components it paints
 * (`controls-all.ts` for the console; the public pages only theirs, to stay small) and calls
 * `bindControls()` before it paints; `controls.ts` builds their markup.
 *
 * - Only the components a page uses are bundled (no autoloader: nothing comes from a CDN).
 * - Their icons (select chevron, check mark, steppers) come from the bundle: Web Awesome's own
 *   «system» library fetches `data:` URLs, which the pages' CSP (`connect-src 'self'`) refuses,
 *   so the same SVGs are drawn in place instead.
 * - Spanish texts for the components' own buttons and messages.
 * - The page's `aria-describedby`, `aria-invalid` and `aria-label` on a control reach the
 *   input inside it (in its shadow root), so a field's error and hint are still read with it.
 * - A select opened again while its list is still closing opens once it closed (#255,
 *   `select-closing.ts`): otherwise the closing hides the list the person just opened.
 */
import '@awesome.me/webawesome/dist/translations/es.js';
import { registerIconLibrary } from '@awesome.me/webawesome/dist/components/icon/library.js';
import { icons } from '@awesome.me/webawesome/dist/components/icon/library.system.js';
import { CONTROL_TAGS } from './controls.ts';
import { holdOpeningWhileClosing } from './select-closing.ts';

type Variant = keyof typeof icons;
let parser: DOMParser | null = null;

/** Draws a system icon from the bundled SVG source (no fetch). */
function drawIcon(svg: SVGElement, host?: { name?: string; variant?: string }) {
  const variant = (host?.variant && host.variant in icons ? host.variant : 'solid') as Variant;
  const set = icons[variant] as Record<string, string>;
  const regular = icons.regular as Record<string, string>;
  const source = set[host?.name ?? ''] ?? regular[host?.name ?? ''];
  if (!source) return;
  parser ??= new DOMParser();
  const doc = parser.parseFromString(source, 'image/svg+xml').documentElement;
  svg.setAttribute('viewBox', doc.getAttribute('viewBox') ?? '0 0 512 512');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.replaceChildren(...[...doc.children].map((c) => document.importNode(c, true)));
}

/**
 * A native text or number input answers '' while empty; Web Awesome's answer null until the
 * person types. The pages trim and compare those values, so the layer keeps the native answer.
 */
const patched = new Set<string>();
function nativeEmptyValue(tag: string) {
  const proto = customElements.get(tag)?.prototype;
  const d = proto && Object.getOwnPropertyDescriptor(proto, 'value');
  const get = d?.get;
  if (!proto || !d || !get || patched.has(tag)) return;
  patched.add(tag);
  Object.defineProperty(proto, 'value', {
    ...d,
    get(this: HTMLElement) {
      return get.call(this) ?? '';
    },
  });
}
for (const tag of ['wa-input', 'wa-number-input']) {
  if (customElements.get(tag)) nativeEmptyValue(tag);
  else void customElements.whenDefined(tag).then(() => nativeEmptyValue(tag));
}

registerIconLibrary('system', {
  // "Sprite sheet" mode renders an <svg> in place and hands it to the mutator: no request.
  spriteSheet: true,
  resolver: (name) => `#${name}`,
  mutator: drawIcon,
});

const SELECTOR = CONTROL_TAGS.join(',');
/** The attributes of the page that the inner control must carry. */
const FORWARDED = ['aria-describedby', 'aria-invalid', 'aria-label', 'aria-required'] as const;

type Control = HTMLElement & { updateComplete?: Promise<unknown> };

/** The focusable control inside a component (input, textarea, combobox, checkbox). */
const innerOf = (el: Element) =>
  el.shadowRoot?.querySelector<HTMLElement>(
    'input:not([type="hidden"]), textarea, [role="combobox"], [role="checkbox"], [role="switch"], [role="radiogroup"]',
  ) ??
  (el.shadowRoot?.querySelector<HTMLElement>('[part~="form-control-input"]') || null);

/** Copies the page's ARIA of one control onto its inner control. */
export async function forwardAria(el: Control): Promise<void> {
  await el.updateComplete;
  const inner = innerOf(el);
  if (!inner) return;
  const ids = (el.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean);
  const root = el.getRootNode() as Document | ShadowRoot;
  const described = ids
    .map((id) => root.getElementById?.(id) ?? document.getElementById(id))
    .filter((x): x is HTMLElement => x !== null);
  const hint = el.shadowRoot?.getElementById('hint');
  const withDescriptions = inner as HTMLElement & { ariaDescribedByElements?: Element[] | null };
  if (described.length && 'ariaDescribedByElements' in withDescriptions)
    withDescriptions.ariaDescribedByElements = hint ? [...described, hint] : described;
  for (const name of FORWARDED.slice(1)) {
    const v = el.getAttribute(name);
    if (v === null) inner.removeAttribute(name);
    else inner.setAttribute(name, v);
  }
}

const watched = new WeakSet<Node>();

/**
 * Keeps the controls under `root` (the whole page by default) wired as they are painted and
 * as the page marks them invalid or described. Called once per page.
 */
export function bindControls(root: Document | HTMLElement = document): void {
  // The components were defined by the page's imports by now: their values answer '' at once.
  for (const tag of ['wa-input', 'wa-number-input']) nativeEmptyValue(tag);
  if (watched.has(root)) return;
  watched.add(root);
  holdOpeningWhileClosing(root);
  const scan = (node: ParentNode) => {
    if (node instanceof Element && node.matches(SELECTOR)) void forwardAria(node as Control);
    for (const el of node.querySelectorAll?.(SELECTOR) ?? []) void forwardAria(el as Control);
  };
  scan(root);
  new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'attributes') {
        const t = r.target as Element;
        if (t.matches(SELECTOR)) void forwardAria(t as Control);
      } else for (const n of r.addedNodes) if (n instanceof Element) scan(n);
    }
  }).observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [...FORWARDED],
  });
}
